import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  getChecklist,
  getOnboarding,
} from "./service/CompanyDocumentsService";
import DocumentChecklist from "./components/DocumentChecklist";
import DocumentUploadModal from "./components/DocumentUploadModal";
import DocumentHistoryModal from "./components/DocumentHistoryModal";
import NameChangePanel from "./components/NameChangePanel";
import OnboardingContacts from "./components/OnboardingContacts";
import OnboardingShareholderForm from "./components/OnboardingShareholderForm";
import CompanyDocumentsLanguageToggle from "./components/CompanyDocumentsLanguageToggle";
import {
  CompanyDocumentsLanguageProvider,
  useCompanyDocumentsLanguage,
} from "./i18n/CompanyDocumentsLanguageContext";

const founderItems = (record, t) =>
  record.items
    .filter(({ document_type }) =>
      record.requirement_type === "INDIVIDUAL"
        ? document_type.code === "KTP_SHAREHOLDER"
        : ["AD_SHAREHOLDER", "AKTA_SHAREHOLDER"].includes(
            document_type.code,
          ),
    )
    .map((item) => ({
      ...item,
      requirementLabel:
        record.requirement_type === "LEGAL_ENTITY"
          ? t("client.common.requiredFounderLegal")
          : t("client.common.required"),
      document_type: {
        ...item.document_type,
        is_required: record.requirement_type === "INDIVIDUAL",
      },
    }));

const issueLabel = (item, prefix = "", t) => {
  if (!item.latest) return `${prefix}${item.document_type.name}`;

  if (item.status === "NEED_REVISION") {
    return `${prefix}${item.document_type.name} — ${t(
      "client.common.needsRevision",
    )}`;
  }

  return "";
};

const requiredDocumentIssues = (
  items,
  prefix = "",
  t,
) =>
  items
    .filter((item) => item.document_type.is_required)
    .map((item) => issueLabel(item, prefix, t))
    .filter(Boolean);

const founderIssues = (record, t) => {
  const prefix = t(
    "client.founder.issuePrefix",
    { name: record.name },
  );

  if (record.requirement_type === "INDIVIDUAL") {
    const ktp = record.items.find(
      (item) => item.document_type.code === "KTP_SHAREHOLDER",
    );

    if (!ktp) return [`${prefix}KTP`];

    const issue = issueLabel(
      ktp,
      prefix,
      t,
    );
    return issue ? [issue] : [];
  }

  const alternatives = record.items.filter((item) =>
    ["AD_SHAREHOLDER", "AKTA_SHAREHOLDER"].includes(
      item.document_type.code,
    ),
  );

  const acceptable = alternatives.find(
    (item) => item.latest && item.status !== "NEED_REVISION",
  );

  if (acceptable) return [];

  const submitted = alternatives.find((item) => item.latest);

  if (submitted) {
    return [
      `${prefix}${submitted.document_type.name} — ${t(
        "client.common.needsRevision",
      )}`,
    ];
  }

  return [
    `${prefix}${t(
      "client.founder.alternativeDocument",
    )}`,
  ];
};

function CompletionSummary({ progress, issues }) {
  const { t } =
    useCompanyDocumentsLanguage();

  const actionable = [...new Set(issues)];
  const complete = progress.complete && actionable.length === 0;

  return (
    <div className="rounded-lg border bg-[#F8FAFC] p-4 min-w-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-semibold">
            {complete
              ? t("client.completion.complete")
              : t("client.completion.needsAttention")}
          </p>

          <p className="text-sm text-[#5F737F]">
            {complete
              ? t(
                  "client.completion.completeDescription",
                )
              : t(
                  "client.completion.attentionDescription",
                  { count: actionable.length },
                )}
          </p>
        </div>

        <Chip
          size="small"
          color={complete ? "success" : "warning"}
          variant="outlined"
          label={
            complete
              ? t("client.completion.complete")
              : t("client.completion.actionRequired")
          }
        />
      </div>

      {!complete && (
        <div className="mt-3 flex flex-wrap gap-2">
          {actionable.slice(0, 10).map((item) => (
            <span
              key={item}
              className="max-w-full rounded-full border bg-white px-3 py-1 text-xs break-words"
            >
              {item}
            </span>
          ))}

          {actionable.length > 10 && (
            <span className="rounded-full border bg-white px-3 py-1 text-xs">
              {t(
                "client.completion.more",
                { count: actionable.length - 10 },
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CompanyDocumentsPageContent({ setLayoutAction }) {
  const { t } =
    useCompanyDocumentsLanguage();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [upload, setUpload] = useState(null);
  const [history, setHistory] = useState(null);
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const refresh = async () => {
    setError("");

    try {
      const [checklist, onboarding] = await Promise.all([
        getChecklist(),
        getOnboarding(),
      ]);

      setData({ checklist, onboarding });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLayoutAction(null);
    refresh();
  }, [setLayoutAction]);

  const openUpload = (
    items,
    section,
    record,
    documentType,
  ) =>
    setUpload({
      documentType,
      allowedTypeIds: items.map(
        (item) => item.document_type.id,
      ),
      scope: {
        section,
        ...(record
          ? { onboarding_shareholder_id: record.id }
          : {}),
      },
      shareholderName: record
        ? `${record.name} (FOUNDER ${record.id})`
        : undefined,
    });

  const checklist = (items, section, record) => (
    <DocumentChecklist
      items={items}
      onUpload={(type) =>
        openUpload(items, section, record, type)
      }
      onHistory={(item) =>
        setHistory({
          ...item,
          onboardingShareholderId: record?.id,
        })
      }
    />
  );

  const current = data?.checklist;
  const onboarding = data?.onboarding;

  const founders = useMemo(
    () =>
      (onboarding?.records || []).filter(
        (row) => row.scope === "FOUNDER",
      ),
    [onboarding],
  );

  const issues = useMemo(() => {
    if (!current || !onboarding) {
      return {
        A: [],
        C: [],
      };
    }

    const bySection = (section) =>
      current.items.filter(
        (item) => item.document_type.section === section,
      );

    const sectionA = requiredDocumentIssues(
      bySection("A"),
      "",
      t,
    );

    const sectionC = requiredDocumentIssues(
      bySection("C"),
      "",
      t,
    );

    const contactsC = onboarding.contacts.filter(
      (contact) => contact.section === "C",
    );

    if (!contactsC.length) {
      sectionC.push(
        t("client.contacts.issueName"),
      );
      sectionC.push(
        t("client.contacts.issueEmail"),
      );
      sectionC.push(
        t("client.contacts.issuePhone"),
      );
    }

    if (!founders.length) {
      sectionA.push(
        t("client.founder.missingOwner"),
      );
    } else {
      founders.forEach((row) =>
        sectionA.push(
          ...founderIssues(row, t),
        ),
      );
    }

    return {
      A: sectionA,
      C: sectionC,
    };
  }, [current, onboarding, founders, t]);

  if (loading) {
    return (
      <div className="p-10 text-center">
        <CircularProgress />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={refresh}>
            {t("client.common.retry")}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const renderFounders = () => {
    const renderForm = (record) => (
      <OnboardingShareholderForm
        key={`FOUNDER-${record?.id || "new"}`}
        scope="FOUNDER"
        record={record}
        onCancel={() => setForm(null)}
        onSaved={async () => {
          setForm(null);
          await refresh();
        }}
      />
    );

    const renderFounder = (record) => {
      const items = founderItems(record, t);

      return (
        <div
          key={record.id}
          className="rounded-lg border p-4 min-w-0"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <h3 className="font-semibold break-words">
                {record.name} ·{" "}
                {record.requirement_type === "INDIVIDUAL"
                  ? t("client.common.individual")
                  : t("client.common.legalEntity")}
              </h3>
            </div>

            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                size="small"
                onClick={() =>
                  setForm({
                    scope: "FOUNDER",
                    record,
                  })
                }
              >
                {t("client.common.edit")}
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  openUpload(items, "A", record)
                }
              >
                {t("client.common.bulkUpload")}
              </Button>
            </div>
          </div>

          {form?.scope === "FOUNDER" &&
            form?.record?.id === record.id &&
            renderForm(record)}

          {record.requirement_type ===
            "LEGAL_ENTITY" && (
            <p className="my-3 text-sm text-[#5F737F]">
              {t(
                "client.founder.legalInstruction",
              )}
            </p>
          )}

          <div className="mt-3">
            {checklist(items, "A", record)}
          </div>
        </div>
      );
    };

    return (
      <div className="grid gap-4 min-w-0">
        <div>
          <Button
            variant="outlined"
            onClick={() =>
              setForm({
                scope: "FOUNDER",
                record: null,
              })
            }
          >
            {t("client.founder.add")}
          </Button>
        </div>

        {form?.scope === "FOUNDER" &&
          !form.record &&
          renderForm(null)}

        {!founders.length && (
          <p className="text-sm text-[#5F737F]">
            {t("client.founder.empty")}
          </p>
        )}

        {founders.map(renderFounder)}
      </div>
    );
  };

  const renderSection = (section) => {
    const isCorporate = section === "A";

    const items = current.items.filter(
      (item) =>
        item.document_type.section === section,
    );

    const contacts =
      section === "C"
        ? onboarding.contacts.filter(
            (contact) => contact.section === "C",
          )
        : [];

    return (
      <section className="bg-white shadow-sm rounded-lg p-4 md:p-5 min-w-0 grid gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-bold">
              {isCorporate
                ? t("client.corporate.title")
                : t("client.management.title")}
            </h2>

            <p className="mt-1 text-sm text-[#5F737F]">
              {isCorporate
                ? t("client.corporate.description")
                : t("client.management.description")}
            </p>
          </div>

          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              openUpload(items, section)
            }
          >
            {t("client.common.bulkUpload")}
          </Button>
        </div>

        {!isCorporate && (
          <div className="rounded-lg border bg-[#F8FAFC] p-3 text-sm text-[#334155]">
            <p>
              <strong>
                {t(
                  "client.management.directorsTitle",
                )}
              </strong>{" "}
              {t(
                "client.management.directorsInstruction",
              )}
            </p>

            <p className="mt-2">
              <strong>
                {t(
                  "client.management.commissionersTitle",
                )}
              </strong>{" "}
              {t(
                "client.management.commissionersInstruction",
              )}
            </p>
          </div>
        )}

        <CompletionSummary
          progress={
            isCorporate
              ? onboarding.corporate_credentials
              : onboarding.management
          }
          issues={issues[section]}
        />

        {checklist(items, section)}

        {!isCorporate && (
          <OnboardingContacts
            key={JSON.stringify(contacts)}
            section="C"
            contacts={contacts}
            onSaved={refresh}
          />
        )}

        {isCorporate && (
          <div className="grid gap-4 border-t pt-5">
            <div>
              <h3 className="font-semibold">
                {t("client.corporate.founderTitle")}
              </h3>

              <p className="mt-1 text-sm text-[#5F737F]">
                {t(
                  "client.corporate.founderDescription",
                )}
              </p>
            </div>

            {renderFounders()}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="grid gap-3 min-w-0">
      <div className="flex justify-end">
        <CompanyDocumentsLanguageToggle />
      </div>

      {error && (
        <Alert
          severity="error"
          action={
            <Button onClick={refresh}>{t("client.common.retry")}</Button>
          }
        >
          {error}
        </Alert>
      )}

      <div className="rounded-lg border bg-white overflow-hidden min-w-0">
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            setActiveTab(value);
            setForm(null);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={t(
            "client.sections.control",
          )}
        >
          <Tab
            label={t(
              "client.sections.profile",
            )}
            sx={{
              borderRight: "1px solid #E2E8F0",
            }}
          />

          <Tab
            label={t(
              "client.sections.corporate",
            )}
            sx={{
              borderRight: "1px solid #E2E8F0",
            }}
          />

          <Tab
            label={t(
              "client.sections.management",
            )}
          />
        </Tabs>
      </div>

      {activeTab === 0 && (
        <section className="min-w-0">
          <NameChangePanel />
        </section>
      )}

      {activeTab === 1 && renderSection("A")}

      {activeTab === 2 && renderSection("C")}

      {upload && (
        <DocumentUploadModal
          {...upload}
          onClose={() => setUpload(null)}
          onSaved={() => {
            setUpload(null);
            refresh();
          }}
        />
      )}

      {history && (
        <DocumentHistoryModal
          documentType={history.document_type}
          latestId={history.latest.id}
          onboardingShareholderId={
            history.onboardingShareholderId
          }
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  );
}

export default function CompanyDocumentsPage(props) {
  return (
    <CompanyDocumentsLanguageProvider>
      <CompanyDocumentsPageContent {...props} />
    </CompanyDocumentsLanguageProvider>
  );
}
